import ErrorAlert from "@/components/ErrorAlert";
import { useTranslation } from "@/i18n";
import { useUser } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import ActionButtons from "./ActionButtons";
import MetadataSection from "./MetadataSection";
import ProcessHeader from "./ProcessHeader";
import ProcessSteps from "./ProcessSteps";

/**
 * Props for the DocumentReview component.
 */
interface DocumentReviewProps {
  /** Whether the component is in a busy state */
  readonly busy: boolean;
  /** Whether a re-run operation is in progress */
  readonly rerunning: boolean;
  /** Whether the proceed operation is in progress */
  readonly proceeding: boolean;
  /** Whether data is currently streaming */
  readonly streaming: boolean;
  /** Callback to re-run the analysis */
  readonly onRerun: () => void;
  /** Callback to proceed to next step */
  readonly onProceed: () => void;
  /** Callback to go back to file selection */
  readonly onBack: () => void;
}

/**
 * Extracted process data structure.
 */
interface ExtractedData {
  readonly processMetadata?: {
    processName?: string;
    processId?: string;
    description?: string;
    isGroup?: boolean;
    parentProcessId?: string;
  };
  readonly catalog?: {
    departments?: Array<{
      id: string;
      name: string;
      description?: string;
      linkedId?: number;
    }>;
    roles?: Array<{
      id: string;
      name: string;
      description?: string;
      linkedId?: number;
    }>;
    resources?: Array<{
      id: string;
      name: string;
      type?: string;
      description?: string;
      linkedId?: number;
    }>;
    locations?: Array<{
      id: string;
      name: string;
      description?: string;
      linkedId?: number;
    }>;
    working_groups?: Array<{
      id: string;
      name: string;
      description?: string;
      linkedId?: number;
    }>;
    requirements?: Array<{
      id: string;
      name: string;
      description?: string;
      chapters?: unknown[];
      linkedId?: number;
    }>;
  };
  readonly model?: {
    nodes?: Array<{
      id?: string;
      name?: string;
      label?: string;
      type?: string;
      description?: string;
      text?: string;
    }>;
    edges?: Array<{
      from?: string;
      source?: string;
      start?: string;
      sourceId?: string;
      to?: string;
      target?: string;
      end?: string;
      targetId?: string;
    }>;
  };
}

/**
 * Document review component that displays extracted process information.
 * Shows process metadata, catalog items (departments, roles, resources, etc.),
 * and process steps in a readable format.
 *
 * Users can review the extracted data and either:
 * - Re-run the analysis if results are unsatisfactory
 * - Proceed to CSV generation if satisfied
 * - Go back to file selection
 *
 * Requires a Microsoft organization account to proceed.
 *
 * @param props - Component props
 * @returns Process review UI with action buttons
 */
export default function DocumentReview({
  busy,
  rerunning,
  proceeding,
  streaming,
  onRerun,
  onProceed,
  onBack,
}: DocumentReviewProps) {
  const { t } = useTranslation();
  const { user } = useUser();
  const [localError, setLocalError] = useState<string | null>(null);

  // Check if user has Microsoft account connected
  const hasMicrosoftAccount =
    user?.externalAccounts?.[0]?.provider === "microsoft";

  // Read extracted data from localStorage
  const extractedDataStr = localStorage.getItem("aiio_extracted_data");
  const extracted = extractedDataStr ? JSON.parse(extractedDataStr) : null;

  // Parse extracted data
  const data = extracted as ExtractedData;
  const pm = data?.processMetadata || {};
  const catalog = data?.catalog || {};
  const model = data?.model || {};
  const processName = pm?.processName || pm?.processId || t("documentReview.defaultProcessName");

  // Initialize form with extracted data including catalog and nodes
  const methods = useForm({
    defaultValues: {
      processName: processName,
      description: pm?.description || "",
      departments: catalog?.departments || [],
      roles: catalog?.roles || [],
      resources: catalog?.resources || [],
      locations: catalog?.locations || [],
      working_groups: catalog?.working_groups || [],
      requirements: catalog?.requirements || [],
      nodes: model?.nodes || [],
    },
  });

  // Watch form values for changes
  const formValues = methods.watch();

  // Initialize form when component mounts
  useEffect(() => {
    if (extracted) {
      methods.reset({
        processName: processName,
        description: pm?.description || "",
        departments: catalog?.departments || [],
        roles: catalog?.roles || [],
        resources: catalog?.resources || [],
        locations: catalog?.locations || [],
        working_groups: catalog?.working_groups || [],
        requirements: catalog?.requirements || [],
        nodes: model?.nodes || [],
      });
    }
  }, []); // Only run once on mount

  // Update localStorage when form values change
  useEffect(() => {
    if (extracted && formValues.processName !== undefined) {
      try {
        const updatedData = {
          ...data,
          processMetadata: {
            ...pm,
            processName: formValues.processName,
            description: formValues.description,
          },
          catalog: {
            departments: formValues.departments,
            roles: formValues.roles,
            resources: formValues.resources,
            locations: formValues.locations,
            working_groups: formValues.working_groups,
            requirements: formValues.requirements,
          },
          model: {
            ...model,
            nodes: formValues.nodes,
          },
        };
        localStorage.setItem(
          "aiio_extracted_data",
          JSON.stringify(updatedData)
        );
        setLocalError(null); // Clear any previous errors on successful save
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : t("documentReview.unknownError");
        setLocalError(errorMessage);
        console.error(
          "Failed to update extracted data in localStorage:",
          error
        );
      }
    }
  }, [formValues]);

  if (!extracted) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <div className="text-[#18181b] rounded-2xl w-[852px] h-[794px] flex flex-col mx-auto">
        <div className="overflow-y-auto max-h-full space-y-8 flex-1 custom-scrollbar px-2">
          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-xl font-semibold">
              {t("documentReview.overview")}
            </h1>
          </div>

          {/* Error Alert */}
          {localError && <ErrorAlert error={localError} />}

          {/* Process Title and Description */}
          <ProcessHeader />

          {/* Metadata Section */}
          <MetadataSection catalog={catalog} />

          {/* Process Steps */}
          <ProcessSteps model={model} />
        </div>

        {/* Bottom Actions */}
        <ActionButtons
          busy={busy}
          rerunning={rerunning}
          proceeding={proceeding}
          streaming={streaming}
          hasMicrosoftAccount={hasMicrosoftAccount}
          onRerun={onRerun}
          onProceed={onProceed}
          onBack={onBack}
        />
      </div>
    </FormProvider>
  );
}
